/**
 * Menüwahl im Paket, Betreiber-Seite (sql/771, docs/KONZEPT-PAKET-MENUEWAHL.md Ablauf 1, Mockup „Menüwahl im Paket“, Bild 1):
 * je Menü die Art (vegan, vegetarisch, Fisch/Fleisch) und der Aufpreis je Gast, dazu der Schalter am Paket
 * „Gast wählt zwischen diesen Menüs“. Den Schalter gibt es erst ab zwei Menüs. Der Paketpreis bleibt gleich; nur Menüs mit
 * Aufpreis ändern den Betrag. Gespeichert wird sofort beim Ändern; Meldungen des Servers (zum Beispiel „jedes Menü braucht
 * eine Art“) stehen ruhig als Satz darunter, nicht als Fehlerkasten.
 */
import { useState } from "react";
import { Card, Input, Select, Toggle } from "./components";
import { centsToEuros, parseGermanEurosToCents } from "./format";

export const MENUE_ART_OPTIONEN = [
  { value: "", label: "Art wählen" },
  { value: "fisch_fleisch", label: "Fisch/Fleisch" },
  { value: "vegetarisch", label: "vegetarisch" },
  { value: "vegan", label: "vegan" },
];

export interface MenuewahlMenue {
  id: string;
  name: string;
  art?: string | null;
  aufpreisPpCents?: number;
}

export function PaketMenuewahlKarte({
  menus,
  gastWaehlt,
  onSchalter,
  onMenue,
  meldung,
}: {
  menus: MenuewahlMenue[];
  gastWaehlt: boolean;
  onSchalter: (an: boolean) => void;
  onMenue: (menuId: string, patch: { art?: string | null; aufpreisPpCents?: number }) => void;
  /** Ruhige Meldung des Servers zur Menüwahl (422), sonst null. */
  meldung: string | null;
}) {
  const [aufpreis, setAufpreis] = useState<Record<string, string>>({});
  const ab2 = menus.length >= 2;
  return (
    <Card className="p-5" data-testid="paket-menuewahl">
      <h2 className="mb-1 font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">Art und Aufpreis je Menü</h2>
      <p className="mb-3 font-body-sm text-[12.5px] text-secondary dark:text-secondary-fixed-dim">
        {ab2 ? "Für die Menüwahl braucht jedes Menü eine Art. Der Aufpreis gilt je Gast gegenüber dem Paketpreis." : "Mit nur einem Menü gibt es keine Wahl. Legen Sie ein zweites Menü an, wenn der Gast zwischen Menüs wählen soll."}
      </p>
      <div className="grid gap-2">
        {menus.map((m) => (
          <div key={m.id} className="grid items-center gap-3 rounded-[10px] border border-outline-variant/40 px-3 py-2.5 sm:grid-cols-[minmax(0,1fr)_170px_150px] dark:border-outline/40" data-testid="paket-menue-zeile" data-menu-name={m.name}>
            <span className="min-w-0 break-words font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">{m.name}</span>
            <Select
              value={m.art ?? ""}
              options={MENUE_ART_OPTIONEN}
              ariaLabel={`Art Menü ${m.name}`}
              testId={`paket-menue-art-${m.id}`}
              onValueChange={(v) => onMenue(m.id, { art: v === "" ? null : v })}
            />
            <div className="flex items-center gap-2">
              <Input
                inputMode="decimal"
                aria-label={`Aufpreis je Gast Menü ${m.name}`}
                value={aufpreis[m.id] ?? centsToEuros(m.aufpreisPpCents ?? 0)}
                onChange={(e) => setAufpreis((a) => ({ ...a, [m.id]: e.target.value }))}
                onBlur={(e) => {
                  const cents = parseGermanEurosToCents(e.target.value);
                  if (cents == null || cents < 0) {
                    setAufpreis((a) => ({ ...a, [m.id]: centsToEuros(m.aufpreisPpCents ?? 0) }));
                    return;
                  }
                  setAufpreis((a) => ({ ...a, [m.id]: centsToEuros(cents) }));
                  if (cents !== (m.aufpreisPpCents ?? 0)) onMenue(m.id, { aufpreisPpCents: cents });
                }}
              />
              <span className="shrink-0 font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">€ je Gast</span>
            </div>
          </div>
        ))}
      </div>
      {ab2 && (
        <div className="mt-3 flex items-start gap-3 rounded-[10px] border border-outline-variant/40 p-3 dark:border-outline/40" data-testid="paket-menuewahl-schalter">
          <Toggle checked={gastWaehlt} onChange={onSchalter} label="Gast wählt zwischen diesen Menüs" />
          <div className="min-w-0">
            <b className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">Gast wählt zwischen diesen Menüs</b>
            <p className="mt-0.5 font-body-sm text-[12.5px] text-secondary dark:text-secondary-fixed-dim">
              Der Gast verteilt seine Gäste auf die {menus.length} Menüs. Der Paketpreis bleibt gleich; ein Menü mit Aufpreis kostet je Gast mehr. Aus: genau ein Menü ist aktiv, wie bisher.
            </p>
          </div>
        </div>
      )}
      {meldung && (
        <p className="mt-3 rounded-[10px] bg-[#F7ECD3] px-3 py-2 font-body-sm text-[12.5px] text-[#6F4A0A] dark:bg-[#8A5A0B]/25 dark:text-[#E3B65E]" role="status" data-testid="paket-menuewahl-meldung">
          {meldung}
        </p>
      )}
    </Card>
  );
}
